import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Edit2, Check, X, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './HealthInfoPage.module.css';

interface HealthData {
  biologicalSex: string;
  ageRange: string;
  heightFeet: number;
  heightInches: number;
  currentWeight: number;
  goal: string;
  targetWeight: number;
  activityLevel: string;
  eatingStyle: string;
  goalCalories: number | null;
  customGoalCalories: number | null;
  goalProtein: number | null;
  goalCarbs: number | null;
  goalFat: number | null;
  customGoalProtein: number | null;
  customGoalCarbs: number | null;
  customGoalFat: number | null;
  foodAllergies: string[];
  foodsToAvoid: string[];
  healthConsiderations: string[];
  eatingChallenges: string[];
}

const BIOLOGICAL_SEX_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary (little to no exercise)' },
  { value: 'lightly_active', label: 'Lightly active (1-2 days/week)' },
  { value: 'moderately_active', label: 'Moderately active (3-4 days/week)' },
  { value: 'very_active', label: 'Very active (5-6 days/week)' },
  { value: 'extremely_active', label: 'Extremely active (daily intense exercise)' },
];
const GOALS = [
  { value: 'lose_weight', label: 'Lose weight' },
  { value: 'maintain_weight', label: 'Maintain weight' },
  { value: 'gain_muscle', label: 'Gain muscle' },
  { value: 'improve_health', label: 'Improve overall health' },
  { value: 'more_energy', label: 'Have more energy' },
  { value: 'eat_healthier', label: 'Eat healthier' },
];
const EATING_STYLES = [
  { value: 'no_restrictions', label: 'No restrictions' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'keto_low_carb', label: 'Keto/Low-carb' },
  { value: 'paleo', label: 'Paleo' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'flexitarian', label: 'Flexitarian' },
];
const HEALTH_CONSIDERATION_OPTIONS = [
  'Diabetes',
  'High blood pressure',
  'Heart disease',
  'High cholesterol',
  'Thyroid condition',
  'PCOS',
  'Digestive issues',
  'Autoimmune condition',
];
const EATING_CHALLENGE_OPTIONS = [
  'Lack of time',
  'Budget constraints',
  'Cravings',
  'Stress eating',
  'Social situations',
  'Cooking skills',
  'Picky eating',
  'Portion control',
];
const FOODS_TO_AVOID_OPTIONS = [
  'Red meat',
  'Pork',
  'Shellfish',
  'Eggs',
  'Dairy',
  'Gluten',
  'Soy',
  'Processed foods',
];
const FOOD_ALLERGY_OPTIONS = [
  'Peanuts',
  'Tree nuts',
  'Dairy',
  'Eggs',
  'Wheat/Gluten',
  'Soy',
  'Fish',
  'Shellfish',
];

// Mifflin-St Jeor BMR calculation
function calculateBMR(sex: string, weightLbs: number, heightFeet: number, heightInches: number, age: number): number {
  const weightKg = weightLbs / 2.205;
  const heightCm = (heightFeet * 12 + heightInches) * 2.54;
  
  if (sex === 'male') {
    return (10 * weightKg) + (6.25 * heightCm) - (5 * age) + 5;
  } else {
    // Female or prefer_not_to_say uses female formula (more conservative)
    return (10 * weightKg) + (6.25 * heightCm) - (5 * age) - 161;
  }
}

// Activity multipliers for TDEE
function getActivityMultiplier(activityLevel: string): number {
  switch (activityLevel) {
    case 'sedentary': return 1.2;
    case 'lightly_active': return 1.375;
    case 'moderately_active': return 1.55;
    case 'very_active': return 1.725;
    case 'extremely_active': return 1.9;
    default: return 1.55; // Default to moderately active
  }
}

// Goal adjustment multiplier
function getGoalMultiplier(goal: string): number {
  switch (goal) {
    case 'lose_weight': return 0.85; // 15% deficit
    case 'gain_muscle': return 1.10; // 10% surplus
    case 'maintain_weight':
    case 'improve_health':
    case 'more_energy':
    case 'eat_healthier':
    default:
      return 1.0; // Maintenance
  }
}

// Calculate daily calories target
function calculateDailyCalories(
  sex: string,
  weightLbs: number,
  heightFeet: number,
  heightInches: number,
  age: number,
  activityLevel: string,
  goal: string
): number {
  const bmr = calculateBMR(sex, weightLbs, heightFeet, heightInches, age);
  const tdee = bmr * getActivityMultiplier(activityLevel);
  const targetCalories = tdee * getGoalMultiplier(goal);
  return Math.round(targetCalories);
}

// Calculate macros from calories (30% protein, 40% carbs, 30% fat)
function calculateMacros(calories: number): { protein: number; carbs: number; fat: number } {
  return {
    protein: Math.round((calories * 0.30) / 4), // 4 cal per gram protein
    carbs: Math.round((calories * 0.40) / 4),   // 4 cal per gram carbs
    fat: Math.round((calories * 0.30) / 9),     // 9 cal per gram fat
  };
}

interface EditableFieldProps {
  label: string;
  fieldKey: string;
  value: any;
  displayValue: string;
  editingField: string | null;
  pendingValue: any;
  onStartEdit: (fieldKey: string, currentValue: any) => void;
  onSave: (fieldKey: string) => void;
  onCancel: () => void;
  onPendingChange: (value: any) => void;
  type: 'select' | 'number' | 'height';
  options?: { value: string; label: string }[];
  unit?: string;
  min?: number;
  max?: number;
  savingField: string | null;
  heightFeet?: number;
  heightInches?: number;
}

function EditableField({
  label,
  fieldKey,
  value,
  displayValue,
  editingField,
  pendingValue,
  onStartEdit,
  onSave,
  onCancel,
  onPendingChange,
  type,
  options = [],
  unit,
  min,
  max,
  savingField,
  heightFeet,
  heightInches,
}: EditableFieldProps) {
  const isEditing = editingField === fieldKey;
  const isSaving = savingField === fieldKey;

  return (
    <div className={styles.editableRow}>
      <div className={styles.editableLabel}>{label}</div>
      <div className={styles.editableContent}>
        {isEditing ? (
          <>
            {type === 'select' && (
              <select
                value={pendingValue}
                onChange={(e) => onPendingChange(e.target.value)}
                className={styles.editableSelect}
                autoFocus
              >
                {options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {type === 'number' && (
              <div className={styles.editableInputWrapper}>
                <input
                  type="number"
                  value={pendingValue}
                  onChange={(e) => onPendingChange(e.target.value)}
                  className={styles.editableInput}
                  min={min}
                  max={max}
                  autoFocus
                />
                {unit && <span className={styles.editableUnit}>{unit}</span>}
              </div>
            )}
            {type === 'height' && (
              <div className={styles.editableHeightInputs}>
                <div className={styles.editableInputWrapper}>
                  <input
                    type="number"
                    value={pendingValue?.feet ?? heightFeet}
                    onChange={(e) => {
                      const newFeet = parseInt(e.target.value) || 0;
                      onPendingChange({ feet: newFeet, inches: pendingValue?.inches ?? heightInches });
                    }}
                    className={styles.editableInput}
                    min={3}
                    max={8}
                    autoFocus
                  />
                  <span className={styles.editableUnit}>ft</span>
                </div>
                <div className={styles.editableInputWrapper}>
                  <input
                    type="number"
                    value={pendingValue?.inches ?? heightInches}
                    onChange={(e) => {
                      const newInches = parseInt(e.target.value) || 0;
                      onPendingChange({ feet: pendingValue?.feet ?? heightFeet, inches: newInches });
                    }}
                    className={styles.editableInput}
                    min={0}
                    max={11}
                  />
                  <span className={styles.editableUnit}>in</span>
                </div>
              </div>
            )}
            <div className={styles.editableActions}>
              <button
                onClick={() => onSave(fieldKey)}
                className={styles.iconButton}
                disabled={isSaving}
                title="Save"
              >
                <Check size={20} />
              </button>
              <button
                onClick={onCancel}
                className={`${styles.iconButton} ${styles.cancelButton}`}
                disabled={isSaving}
                title="Cancel"
              >
                <X size={20} />
              </button>
            </div>
          </>
        ) : (
          <>
            <span className={styles.editableValue}>{displayValue}</span>
            <button
              onClick={() => onStartEdit(fieldKey, type === 'height' ? { feet: heightFeet, inches: heightInches } : value)}
              className={styles.editIcon}
              title="Edit"
            >
              <Edit2 size={20} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

interface EditableCheckboxGridProps {
  label: string;
  fieldKey: string;
  options: string[];
  selectedValues: string[];
  editingField: string | null;
  pendingValue: string[];
  onStartEdit: (fieldKey: string, currentValue: string[]) => void;
  onSave: (fieldKey: string) => void;
  onCancel: () => void;
  onToggle: (value: string) => void;
  savingField: string | null;
}

function EditableCheckboxGrid({
  label,
  fieldKey,
  options,
  selectedValues,
  editingField,
  pendingValue,
  onStartEdit,
  onSave,
  onCancel,
  onToggle,
  savingField,
}: EditableCheckboxGridProps) {
  const isEditing = editingField === fieldKey;
  const isSaving = savingField === fieldKey;
  const displayValues = isEditing ? pendingValue : selectedValues;

  return (
    <div className={styles.checkboxSection}>
      <div className={styles.checkboxHeader}>
        <label className={styles.checkboxSectionLabel}>{label}</label>
        {isEditing ? (
          <div className={styles.editableActions}>
            <button
              onClick={() => onSave(fieldKey)}
              className={styles.iconButton}
              disabled={isSaving}
              title="Save"
            >
              <Check size={20} />
            </button>
            <button
              onClick={onCancel}
              className={`${styles.iconButton} ${styles.cancelButton}`}
              disabled={isSaving}
              title="Cancel"
            >
              <X size={20} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => onStartEdit(fieldKey, selectedValues)}
            className={styles.editIcon}
            title="Edit"
          >
            <Edit2 size={20} />
          </button>
        )}
      </div>
      <div className={styles.checkboxGrid}>
        {options.map((option) => (
          <label
            key={option}
            className={`${styles.checkboxLabel} ${!isEditing ? styles.checkboxDisabled : ''}`}
          >
            <input
              type="checkbox"
              checked={displayValues.includes(option)}
              onChange={() => isEditing && onToggle(option)}
              className={styles.checkbox}
              disabled={!isEditing}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function HealthInfoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [pendingValue, setPendingValue] = useState<any>(null);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [healthData, setHealthData] = useState<HealthData>({
    biologicalSex: '',
    ageRange: '',
    heightFeet: 5,
    heightInches: 6,
    currentWeight: 150,
    goal: 'maintain_weight',
    targetWeight: 150,
    activityLevel: 'moderately_active',
    eatingStyle: 'no_restrictions',
    goalCalories: null,
    customGoalCalories: null,
    goalProtein: null,
    goalCarbs: null,
    goalFat: null,
    customGoalProtein: null,
    customGoalCarbs: null,
    customGoalFat: null,
    foodAllergies: [],
    foodsToAvoid: [],
    healthConsiderations: [],
    eatingChallenges: [],
  });

  useEffect(() => {
    if (!user) return;

    const loadHealthData = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('health_profiles')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (fetchError) {
          console.error('Error loading health data:', fetchError);
          setIsLoading(false);
          return;
        }

        if (!data) {
          navigate('/health-quiz', { replace: true });
          return;
        }

        setHealthData({
          biologicalSex: data.biological_sex || '',
          ageRange: data.age_range || '',
          heightFeet: data.height_feet || 5,
          heightInches: data.height_inches || 6,
          currentWeight: data.current_weight || 150,
          goal: data.goal || 'maintain_weight',
          targetWeight: data.target_weight || 150,
          activityLevel: data.activity_level || 'moderately_active',
          eatingStyle: data.eating_style || 'no_restrictions',
          goalCalories: data.goal_calories ?? null,
          customGoalCalories: data.custom_goal_calories ?? null,
          goalProtein: data.goal_protein ?? null,
          goalCarbs: data.goal_carbs ?? null,
          goalFat: data.goal_fat ?? null,
          customGoalProtein: data.custom_goal_protein ?? null,
          customGoalCarbs: data.custom_goal_carbs ?? null,
          customGoalFat: data.custom_goal_fat ?? null,
          foodAllergies: data.food_allergies || [],
          foodsToAvoid: data.foods_to_avoid || [],
          healthConsiderations: data.health_considerations || [],
          eatingChallenges: data.eating_challenges || [],
        });
      } catch (err) {
        console.error('Error loading health data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadHealthData();
  }, [user, navigate]);

  const startEdit = (fieldKey: string, currentValue: any) => {
    setEditingField(fieldKey);
    setPendingValue(currentValue);
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setPendingValue(null);
  };

  const getDbFieldMapping = (fieldKey: string): Record<string, any> => {
    switch (fieldKey) {
      case 'biologicalSex':
        return { biological_sex: pendingValue };
      case 'ageRange':
        return { age_range: pendingValue };
      case 'height':
        return { height_feet: pendingValue.feet, height_inches: pendingValue.inches };
      case 'currentWeight':
        return { current_weight: parseInt(pendingValue) || 0 };
      case 'goal':
        return { goal: pendingValue };
      case 'targetWeight':
        return { target_weight: parseInt(pendingValue) || 0 };
      case 'activityLevel':
        return { activity_level: pendingValue };
      case 'eatingStyle':
        return { eating_style: pendingValue };
      case 'customGoalCalories':
        return { custom_goal_calories: pendingValue ? parseInt(pendingValue) : null };
      case 'customGoalProtein':
        return { custom_goal_protein: pendingValue ? parseInt(pendingValue) : null };
      case 'customGoalCarbs':
        return { custom_goal_carbs: pendingValue ? parseInt(pendingValue) : null };
      case 'customGoalFat':
        return { custom_goal_fat: pendingValue ? parseInt(pendingValue) : null };
      case 'foodAllergies':
        return { food_allergies: pendingValue };
      case 'foodsToAvoid':
        return { foods_to_avoid: pendingValue };
      case 'healthConsiderations':
        return { health_considerations: pendingValue };
      case 'eatingChallenges':
        return { eating_challenges: pendingValue };
      default:
        return {};
    }
  };

  const saveField = async (fieldKey: string) => {
    if (!user) return;

    setSavingField(fieldKey);
    setError('');
    setSuccess('');

    try {
      const dbFields = getDbFieldMapping(fieldKey);
      const { error: updateError } = await supabase
        .from('health_profiles')
        .update({
          ...dbFields,
          updated_at: new Date().toISOString(),
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      if (fieldKey === 'height') {
        setHealthData((prev) => ({
          ...prev,
          heightFeet: pendingValue.feet,
          heightInches: pendingValue.inches,
        }));
      } else if (['foodAllergies', 'foodsToAvoid', 'healthConsiderations', 'eatingChallenges'].includes(fieldKey)) {
        setHealthData((prev) => ({ ...prev, [fieldKey]: pendingValue }));
      } else if (['currentWeight', 'targetWeight'].includes(fieldKey)) {
        setHealthData((prev) => ({ ...prev, [fieldKey]: parseInt(pendingValue) || 0 }));
      } else if (['customGoalCalories', 'customGoalProtein', 'customGoalCarbs', 'customGoalFat'].includes(fieldKey)) {
        setHealthData((prev) => ({ ...prev, [fieldKey]: pendingValue ? parseInt(pendingValue) : null }));
      } else {
        setHealthData((prev) => ({ ...prev, [fieldKey]: pendingValue }));
      }

      setSuccess('Saved successfully');
      setEditingField(null);
      setPendingValue(null);
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Error saving field:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSavingField(null);
    }
  };

  const toggleCheckboxValue = (value: string) => {
    if (!pendingValue) return;
    if (pendingValue.includes(value)) {
      setPendingValue(pendingValue.filter((v: string) => v !== value));
    } else {
      setPendingValue([...pendingValue, value]);
    }
  };

  const getDisplayLabel = (value: string, options: { value: string; label: string }[]) => {
    return options.find((opt) => opt.value === value)?.label || value || '—';
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <Heart size={28} />
          Health Information
        </h1>
        <p className={styles.subtitle}>Help us personalize your nutrition recommendations</p>
      </header>

      <div className={styles.form}>
        {error && (
          <div className={styles.errorMessage}>
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        {success && <div className={styles.successMessage}>{success}</div>}

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Basic Information</h2>

          <EditableField
            label="Biological Sex"
            fieldKey="biologicalSex"
            value={healthData.biologicalSex}
            displayValue={getDisplayLabel(healthData.biologicalSex, BIOLOGICAL_SEX_OPTIONS)}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="select"
            options={BIOLOGICAL_SEX_OPTIONS}
            savingField={savingField}
          />

          <EditableField
            label="Age"
            fieldKey="ageRange"
            value={healthData.ageRange}
            displayValue={healthData.ageRange ? `${healthData.ageRange} years` : '—'}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="number"
            unit="years"
            min={18}
            max={100}
            savingField={savingField}
          />

          <EditableField
            label="Height"
            fieldKey="height"
            value={{ feet: healthData.heightFeet, inches: healthData.heightInches }}
            displayValue={`${healthData.heightFeet} ft ${healthData.heightInches} in`}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="height"
            heightFeet={healthData.heightFeet}
            heightInches={healthData.heightInches}
            savingField={savingField}
          />

          <EditableField
            label="Current Weight"
            fieldKey="currentWeight"
            value={healthData.currentWeight}
            displayValue={`${healthData.currentWeight} lbs`}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="number"
            unit="lbs"
            min={50}
            max={500}
            savingField={savingField}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Goals & Preferences</h2>

          <EditableField
            label="Your Goal"
            fieldKey="goal"
            value={healthData.goal}
            displayValue={getDisplayLabel(healthData.goal, GOALS)}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="select"
            options={GOALS}
            savingField={savingField}
          />

          <EditableField
            label="Target Weight"
            fieldKey="targetWeight"
            value={healthData.targetWeight}
            displayValue={`${healthData.targetWeight} lbs`}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="number"
            unit="lbs"
            min={50}
            max={500}
            savingField={savingField}
          />

          <EditableField
            label="Activity Level"
            fieldKey="activityLevel"
            value={healthData.activityLevel}
            displayValue={getDisplayLabel(healthData.activityLevel, ACTIVITY_LEVELS)}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="select"
            options={ACTIVITY_LEVELS}
            savingField={savingField}
          />

          <EditableField
            label="Eating Style"
            fieldKey="eatingStyle"
            value={healthData.eatingStyle}
            displayValue={getDisplayLabel(healthData.eatingStyle, EATING_STYLES)}
            editingField={editingField}
            pendingValue={pendingValue}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onPendingChange={setPendingValue}
            type="select"
            options={EATING_STYLES}
            savingField={savingField}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Nutrition Goals</h2>
          <p className={styles.sectionNote}>Calculated values are shown as read-only. Custom values override them.</p>

          {(() => {
            // Calculate calories client-side using user's profile data
            const age = parseInt(healthData.ageRange) || 60;
            const calculatedCalories = calculateDailyCalories(
              healthData.biologicalSex,
              healthData.currentWeight,
              healthData.heightFeet,
              healthData.heightInches,
              age,
              healthData.activityLevel,
              healthData.goal
            );
            
            // Use database value if available, otherwise use calculated
            const displayCalories = healthData.goalCalories ?? calculatedCalories;
            const calculatedMacros = calculateMacros(displayCalories);
            
            const displayProtein = healthData.goalProtein ?? calculatedMacros.protein;
            const displayCarbs = healthData.goalCarbs ?? calculatedMacros.carbs;
            const displayFat = healthData.goalFat ?? calculatedMacros.fat;

            return (
              <>
                <div className={styles.nutritionColumn}>
                  <div className={styles.nutritionItem}>
                    <label className={styles.label}>Daily Calories (Calculated)</label>
                    <div className={styles.nutritionValue}>{displayCalories} cal</div>
                  </div>
                  
                  <EditableField
                    label="Custom Calories Override"
                    fieldKey="customGoalCalories"
                    value={healthData.customGoalCalories}
                    displayValue={healthData.customGoalCalories !== null ? `${healthData.customGoalCalories} cal` : '—'}
                    editingField={editingField}
                    pendingValue={pendingValue}
                    onStartEdit={startEdit}
                    onSave={saveField}
                    onCancel={cancelEdit}
                    onPendingChange={setPendingValue}
                    type="number"
                    unit="cal"
                    min={500}
                    max={10000}
                    savingField={savingField}
                  />
                </div>

                <div className={styles.macrosSection}>
                  <h3 className={styles.macrosTitle}>Macro Goals</h3>
                  <div className={styles.macrosGrid}>
                    <div className={styles.macroItem}>
                      <label className={styles.label}>Protein (Calculated)</label>
                      <div className={styles.nutritionValue}>{displayProtein}g</div>
                    </div>
                    <div className={styles.macroItem}>
                      <label className={styles.label}>Carbs (Calculated)</label>
                      <div className={styles.nutritionValue}>{displayCarbs}g</div>
                    </div>
                    <div className={styles.macroItem}>
                      <label className={styles.label}>Fat (Calculated)</label>
                      <div className={styles.nutritionValue}>{displayFat}g</div>
                    </div>
                  </div>

                  <div className={styles.customMacrosGrid}>
                    <EditableField
                      label="Custom Protein"
                      fieldKey="customGoalProtein"
                      value={healthData.customGoalProtein}
                      displayValue={healthData.customGoalProtein !== null ? `${healthData.customGoalProtein}g` : '—'}
                      editingField={editingField}
                      pendingValue={pendingValue}
                      onStartEdit={startEdit}
                      onSave={saveField}
                      onCancel={cancelEdit}
                      onPendingChange={setPendingValue}
                      type="number"
                      unit="g"
                      min={0}
                      max={500}
                      savingField={savingField}
                    />
                    <EditableField
                      label="Custom Carbs"
                      fieldKey="customGoalCarbs"
                      value={healthData.customGoalCarbs}
                      displayValue={healthData.customGoalCarbs !== null ? `${healthData.customGoalCarbs}g` : '—'}
                      editingField={editingField}
                      pendingValue={pendingValue}
                      onStartEdit={startEdit}
                      onSave={saveField}
                      onCancel={cancelEdit}
                      onPendingChange={setPendingValue}
                      type="number"
                      unit="g"
                      min={0}
                      max={1000}
                      savingField={savingField}
                    />
                    <EditableField
                      label="Custom Fat"
                      fieldKey="customGoalFat"
                      value={healthData.customGoalFat}
                      displayValue={healthData.customGoalFat !== null ? `${healthData.customGoalFat}g` : '—'}
                      editingField={editingField}
                      pendingValue={pendingValue}
                      onStartEdit={startEdit}
                      onSave={saveField}
                      onCancel={cancelEdit}
                      onPendingChange={setPendingValue}
                      type="number"
                      unit="g"
                      min={0}
                      max={500}
                      savingField={savingField}
                    />
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Dietary Restrictions</h2>

          <EditableCheckboxGrid
            label="Food Allergies"
            fieldKey="foodAllergies"
            options={FOOD_ALLERGY_OPTIONS}
            selectedValues={healthData.foodAllergies}
            editingField={editingField}
            pendingValue={pendingValue || []}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onToggle={toggleCheckboxValue}
            savingField={savingField}
          />

          <EditableCheckboxGrid
            label="Foods to Avoid"
            fieldKey="foodsToAvoid"
            options={FOODS_TO_AVOID_OPTIONS}
            selectedValues={healthData.foodsToAvoid}
            editingField={editingField}
            pendingValue={pendingValue || []}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onToggle={toggleCheckboxValue}
            savingField={savingField}
          />
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Health & Challenges</h2>

          <EditableCheckboxGrid
            label="Health Considerations"
            fieldKey="healthConsiderations"
            options={HEALTH_CONSIDERATION_OPTIONS}
            selectedValues={healthData.healthConsiderations}
            editingField={editingField}
            pendingValue={pendingValue || []}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onToggle={toggleCheckboxValue}
            savingField={savingField}
          />

          <EditableCheckboxGrid
            label="Eating Challenges"
            fieldKey="eatingChallenges"
            options={EATING_CHALLENGE_OPTIONS}
            selectedValues={healthData.eatingChallenges}
            editingField={editingField}
            pendingValue={pendingValue || []}
            onStartEdit={startEdit}
            onSave={saveField}
            onCancel={cancelEdit}
            onToggle={toggleCheckboxValue}
            savingField={savingField}
          />
        </div>
      </div>
    </div>
  );
}
